// docs/130 OB3: what the stakes are about shimmers gold on the board, so a party reads the win at a
// glance: the thing to work at (a fire to light) until it is done, a relic lying loose, the squares
// to reach or hold, where a relic or an escorted ally must go, and the ally the party must keep
// standing. A pulsing outline and a soft gold fill on each square, drawn under the units.
//
// The pulse is driven from the scene's update rather than a looping tween: the scene kills every
// tween when a new board arrives (Phaser's killAll destroys them), which would stop it for good.
import Phaser from 'phaser';
import type { Snapshot } from '../../core/sim/types';
import { objectiveMarks } from '../../core/encounter/objectives';

const GOLD = 0xffd166;
const PALE = 0xfff1c1;
/** One pulse, bright to dim and back (ms), and how dim it gets. */
const PULSE_MS = 1800;
const DIMMEST = 0.35;

export class ObjectiveGlow {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly onUpdate?: (time: number) => void;

  constructor(private readonly scene: Phaser.Scene, layer: Phaser.GameObjects.Layer, private readonly cell: number, reduceMotion = false) {
    this.g = scene.add.graphics();
    layer.add(this.g);
    if (!reduceMotion) {
      // a sine in and out from full to DIMMEST and back, as the yoyo tween it replaces
      this.onUpdate = (time: number) => { this.g.setAlpha(1 - (1 - DIMMEST) * (0.5 - 0.5 * Math.cos((time / PULSE_MS) * Math.PI * 2))); };
      scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.stop, this);
      scene.events.once(Phaser.Scenes.Events.DESTROY, this.stop, this);
      this.g.once(Phaser.GameObjects.Events.DESTROY, () => this.stop());
    }
  }

  /** The glow goes (and its pulse stops). */
  destroy(): void {
    this.stop();
    this.g.destroy();
  }

  private stop(): void {
    if (!this.onUpdate) return;
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.stop, this);
    this.scene.events.off(Phaser.Scenes.Events.DESTROY, this.stop, this);
  }

  draw(s: Snapshot | undefined): void {
    const g = this.g;
    const c = this.cell;
    g.clear();
    const { tiles, units } = objectiveMarks(s);
    for (const t of tiles) {
      const x = t.x * c, y = t.y * c;
      g.fillStyle(GOLD, 0.22).fillRect(x, y, c, c);
      g.lineStyle(2, GOLD, 1).strokeRect(x + 1.5, y + 1.5, c - 3, c - 3);
      g.lineStyle(1, PALE, 0.7).strokeRect(x + 4, y + 4, c - 8, c - 8);
    }
    for (const id of units) {
      const u = (s?.units ?? []).find((x) => x.unitId === id);
      if (!u) continue;
      const size = Math.max(1, u.size ?? 1);
      const cx = u.tile.x * c + (c * size) / 2, cy = u.tile.y * c + c * size - 3;
      g.fillStyle(GOLD, 0.22).fillEllipse(cx, cy, c * size * 0.9, 9);
      g.lineStyle(2, GOLD, 1).strokeEllipse(cx, cy, c * size * 0.9, 9);
      g.lineStyle(1, PALE, 0.7).strokeEllipse(cx, cy, c * size * 0.9 + 6, 13);
    }
  }
}
